import { cn } from "@/lib/utils";

export interface WizardStepperProps {
  /** Step labels, in order, for the steps this league actually has. */
  steps: string[];
  /** Index of the step being shown. */
  current: number;
  /** Highest index the operator has reached; later steps stay inert. */
  reached: number;
  onSelect: (index: number) => void;
}

/**
 * Numbered-circle stepper for Guided Setup, matching the one Advanced Setup
 * draws on its tab list: each step is its number and its name, joined to the
 * next by a rule, and the one you are on is the one that is filled in.
 *
 * A step that has been reached is a link back to it. A step that has not is
 * muted and inert — Guided validates each step before it advances, so skipping
 * ahead would land the operator on a step whose answers do not exist yet.
 */
export function WizardStepper({
  steps,
  current,
  reached,
  onSelect,
}: WizardStepperProps) {
  return (
    <nav aria-label="Setup steps" className="flex w-fit items-center">
      {steps.map((label, i) => {
        const isCurrent = i === current;
        const canGo = i <= reached && !isCurrent;
        return (
          <div key={label} className="flex items-center">
            {i > 0 && <span aria-hidden className="h-px w-6 bg-border" />}
            <button
              type="button"
              onClick={() => canGo && onSelect(i)}
              disabled={!canGo}
              aria-current={isCurrent ? "step" : undefined}
              className={cn(
                "flex items-center gap-2 rounded-md px-2 py-1 text-sm transition-colors",
                isCurrent
                  ? "poppins font-semibold text-text-figure"
                  : i <= reached
                    ? "poppins cursor-pointer font-medium text-text-figure hover:text-primary"
                    : "mulish cursor-default font-medium text-text-muted",
              )}
            >
              <span
                aria-hidden
                className={cn(
                  "flex size-5 shrink-0 items-center justify-center rounded-full text-xs font-semibold",
                  isCurrent
                    ? "border border-primary text-primary ring-1 ring-primary/30"
                    : i <= reached
                      ? "border border-primary/40 text-primary"
                      : "border border-border text-text-muted",
                )}
              >
                {i + 1}
              </span>
              {label}
            </button>
          </div>
        );
      })}
    </nav>
  );
}
